import React, { useMemo } from 'react';
import Donut from './Donut';
import { fmt$, signed$, pnlClr, fmtNum } from './fmt';
import { useTheme } from '../../../context/ThemeContext';

// The IBKR (broker) panel — live broker truth behind the Strategy⟷IBKR toggle.
// Fetched lazily by the container only when this panel is opened, so the strategy
// page never hits the gateway on load. 8 broker tiles, a Top Positions donut, a
// broker sector split, and the Open Positions blotter — plus an error/relogin path.
const COLORS = ['#c9a96e', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];
const OTHERS = '#6b7280';

const Kpi = ({ label, value, valueClass = 'text-gray-800 dark:text-gray-100', sub }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-3">
    <div className="text-[9px] uppercase tracking-widest text-gray-400">{label}</div>
    <div className={`mt-1.5 font-mono text-[16px] tabular-nums ${valueClass}`}>{value}</div>
    {sub && <div className="mt-0.5 text-[10px] text-gray-500 truncate">{sub}</div>}
  </div>
);

export default function IbkrPanel({ ibkr, loading, error, onRelogin, reloggingIn, isAdmin }) {
  const acct = ibkr?.account || {};
  const attributed = ibkr?.strategy_attributed || {};
  const positions = useMemo(() => ibkr?.positions || [], [ibkr]);
  const sectors = ibkr?.sectors || [];
  const unattr = ibkr?.unattributed || null;
  const { isDark } = useTheme();
  const palette = useMemo(() => [isDark ? '#c9a96e' : '#3b82f6', ...COLORS.slice(1)], [isDark]);

  const donutSegs = useMemo(() => {
    const sorted = positions.filter((p) => (p.market_value || 0) > 0).sort((a, b) => (b.market_value || 0) - (a.market_value || 0));
    const head = sorted.slice(0, 5).map((p, i) => ({ label: p.symbol, value: p.market_value, color: palette[i % palette.length] }));
    const tail = sorted.slice(5);
    if (tail.length) head.push({ label: `Others (${tail.length})`, value: tail.reduce((s, p) => s + (p.market_value || 0), 0), color: OTHERS });
    return head;
  }, [positions, palette]);

  if (error) {
    return (
      <div className="bg-red-500/[0.08] border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-500 dark:text-red-400">
        ⚠ {error}
        <p className="text-[11px] mt-1 opacity-80">Make sure the IBKR gateway + socat proxy (port 14002) are up on the trading EC2.</p>
        {isAdmin && (
          <button
            onClick={onRelogin}
            disabled={reloggingIn}
            className="mt-2 px-3 py-1.5 text-xs rounded-lg border border-red-400/50 text-red-500 dark:text-red-300 hover:bg-red-500/10 disabled:opacity-50 transition-colors"
          >{reloggingIn ? 'Relogging in…' : 'Relogin gateway'}</button>
        )}
      </div>
    );
  }

  if (loading && !ibkr) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {[...Array(8)].map((_, i) => <div key={i} className="h-[68px] skeleton rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-[220px] skeleton rounded-xl" />
          <div className="h-[220px] skeleton rounded-xl" />
        </div>
        <p className="text-[11px] text-gray-400 italic text-center">Fetching live broker portfolio… (~15s)</p>
      </div>
    );
  }
  if (!ibkr) return null;

  return (
    <div className="space-y-4">
      <p className="text-[10.5px] uppercase tracking-wide text-gray-400">Live broker truth · IBKR gateway</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi label="NAV" value={fmt$(acct.net_liquidation)}
          sub={acct.net_liquidation_incl_dividends != null && acct.net_liquidation_incl_dividends !== acct.net_liquidation
            ? `net liquidation · incl. dividends ${fmt$(acct.net_liquidation_incl_dividends)}` : 'net liquidation'} />
        <Kpi label="Today's P&L" value={signed$(acct.daily_pnl)} valueClass={pnlClr(acct.daily_pnl)} sub="broker mark-to-market" />
        <Kpi label="Gross Position" value={fmt$(acct.gross_position)} sub="gross exposure" />
        <Kpi label="AUM" value={fmt$(acct.aum)} sub="net MV · ex-cash" />
        <Kpi label="Strategy Mkt Value" value={fmt$(attributed.total)} sub={`${fmt$(attributed.market_value)} inv · ${fmt$(attributed.cash)} cash`} />
        <Kpi label="Net Long Value" value={fmt$(acct.net_long_value)} valueClass={pnlClr(acct.net_long_value)} sub="long market value" />
        <Kpi label="Net Short Value" value={fmt$(acct.net_short_value)} valueClass={pnlClr(acct.net_short_value)} sub="short market value" />
        <Kpi label="Net Cash" value={fmt$(acct.total_cash)}
          sub={acct.dividend_receivable != null && (acct.dividend_cash_settled || acct.dividend_receivable)
            ? `cash balance · divs settled ${fmt$(acct.dividend_cash_settled)} · receivable ${fmt$(acct.dividend_receivable)}` : 'total cash balance'} />
      </div>

      {/* Unattributed holdings — broker shares no strategy or reserve owns (sweep candidates) */}
      {unattr && (unattr.n > 0 ? (
        <div className="bg-white dark:bg-[#161b22] border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200">
              Unattributed Holdings <span className="text-gray-400 text-xs">· not owned by any strategy</span>
            </h3>
            <span className="text-amber-500 font-mono text-xs tabular-nums">{unattr.n} · {fmt$(unattr.total_value)}</span>
          </div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 mt-0.5 mb-3">
            broker shares no strategy, reserve, or central book accounts for · candidates to sweep out
          </div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: 540 }}>
              <colgroup><col style={{ width: '20%' }} /><col style={{ width: '14%' }} /><col style={{ width: '20%' }} /><col style={{ width: '16%' }} /><col style={{ width: '18%' }} /><col style={{ width: '12%' }} /></colgroup>
              <thead>
                <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2">Symbol</th>
                  <th className="text-right py-2">Broker</th>
                  <th className="text-right py-2">Attributed</th>
                  <th className="text-right py-2">Unattributed</th>
                  <th className="text-right py-2">Value</th>
                  <th className="text-right py-2"> </th>
                </tr>
              </thead>
              <tbody>
                {unattr.positions.map((u) => (
                  <tr key={u.symbol} className="border-b border-gray-100 dark:border-gray-800/50">
                    <td className="py-2 font-semibold text-gray-800 dark:text-gray-200 truncate">{u.symbol}</td>
                    <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtNum(u.broker_qty)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500">{fmtNum(u.system_qty)}</td>
                    <td className={`py-2 text-right tabular-nums ${u.kind === 'shortfall' ? 'text-red-400' : 'text-amber-500'}`}>
                      {u.unattributed_qty > 0 ? '+' : ''}{fmtNum(u.unattributed_qty)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{fmt$(u.unattributed_value)}</td>
                    <td className="py-2 text-right">
                      <span className={`text-[8.5px] uppercase tracking-wide rounded px-1 py-px border ${
                        u.kind === 'manual' ? 'text-amber-500 border-amber-500/40'
                          : u.kind === 'shortfall' ? 'text-red-400 border-red-400/40'
                          : 'text-gray-400 border-gray-400/40'}`}
                      >{u.kind}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-[10px] text-gray-400 leading-relaxed">
            <span className="text-amber-500">manual</span> = no strategy owns any of it · <span className="text-gray-400">partial</span> = strategy owns some, the rest isn't ·
            {' '}<span className="text-red-400">shortfall</span> = our system thinks it owns more than the broker holds (investigate, don't sweep). "Attributed" = strategy + reserve + central-book hedge; "Unattributed" = broker − attributed — a hand-trade or an unreconciled drift.
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#161b22] border border-green-500/20 rounded-xl px-4 py-3 text-xs text-green-500 dark:text-green-400">
          ✓ Every broker position is owned by a strategy, the reserve, or the central book — nothing unattributed.
        </div>
      ))}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200 mb-1">Top Positions</h3>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 mb-3">broker · by market value</div>
          <div className="flex items-center justify-center">
            {donutSegs.length
              ? <Donut segments={donutSegs} centerLabel={`${positions.length} pos`} size={190} />
              : <div className="text-xs text-gray-400 italic py-6">No open positions.</div>}
          </div>
        </div>
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200 mb-1">Sector Allocation</h3>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 mb-3">broker positions · IBKR</div>
          {sectors.length === 0
            ? <div className="text-xs text-gray-400 italic py-4">No sector data.</div>
            : sectors.slice(0, 8).map((s, i) => (
              <div key={s.sector} className="flex items-center gap-2.5 my-2 text-xs">
                <span className="w-[38%] text-gray-600 dark:text-gray-300 truncate" title={s.sector}>{s.sector}</span>
                <span className="flex-1 h-[7px] rounded-full bg-gray-100 dark:bg-[#20272f] overflow-hidden">
                  <span className="block h-full rounded-full" style={{ width: `${Math.min(100, (s.pct || 0) * 100)}%`, background: palette[i % palette.length] }} />
                </span>
                <span className="w-11 text-right font-mono tabular-nums text-gray-500">{((s.pct || 0) * 100).toFixed(1)}%</span>
              </div>
            ))}
        </div>
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200">Open Positions <span className="text-gray-400 text-xs">(broker blotter)</span></h3>
          <span className="text-[10.5px] uppercase tracking-wide text-gray-400">{positions.length} positions</span>
        </div>
        {positions.length === 0 ? (
          <div className="text-xs text-gray-400 italic py-2">No open positions.</div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: 560 }}>
              <colgroup><col style={{ width: '18%' }} /><col style={{ width: '12%' }} /><col style={{ width: '16%' }} /><col style={{ width: '18%' }} /><col style={{ width: '20%' }} /><col style={{ width: '16%' }} /></colgroup>
              <thead>
                <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2">Symbol</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Avg Cost</th><th className="text-right py-2">Mkt Value</th><th className="text-right py-2">Unrealized P&amp;L</th><th className="text-right py-2">Realized P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const cost = (p.avg_cost || 0) * Math.abs(p.quantity || 0);
                  const upnlPct = cost > 0 && p.unrealized_pnl != null ? (p.unrealized_pnl / cost) * 100 : null;
                  return (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330]">
                      <td className="py-2 font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {p.symbol}<span className="ml-1 text-[9px] text-gray-400 font-normal">{p.sec_type}</span>
                      </td>
                      <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtNum(p.quantity)}</td>
                      <td className="py-2 text-right tabular-nums text-gray-500">{fmt$(p.avg_cost, 2)}</td>
                      <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{p.market_value != null ? fmt$(p.market_value) : '—'}</td>
                      <td className={`py-2 text-right tabular-nums ${pnlClr(p.unrealized_pnl)}`}>
                        {p.unrealized_pnl != null ? signed$(p.unrealized_pnl) : '—'}
                        {upnlPct != null && <span className="text-[9px] ml-1">({upnlPct >= 0 ? '+' : ''}{upnlPct.toFixed(1)}%)</span>}
                      </td>
                      <td className={`py-2 text-right tabular-nums ${pnlClr(p.realized_pnl)}`}>{p.realized_pnl != null ? signed$(p.realized_pnl) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
